import React, { useEffect, useState } from 'react';
import { getErrorMessage } from '@/lib/utils';
import { useI18n } from '../contexts/I18nContext';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { cn } from '@/lib/utils';
import { 
  FileText,
  Plus,
  Pencil,
  Trash2,
  Star,
  Package,
  FolderPlus,
  MoreVertical,
  History,
  TrendingUp,
  TrendingDown,
  Minus,
  ArrowLeft,
  BookOpen,
  MousePointerClick,
  Lightbulb,
  ArrowRight,
  HelpCircle,
  Users,
  Building2,
  Percent,
  Tag,
  Copy,
  Check,
  Loader2
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PricelistsPage = () => {
  const { t } = useI18n();
  const { confirm: confirmAction, ConfirmDialog } = useConfirmDialog();
  const [pricelists, setPricelists] = useState([]);
  const [products, setProducts] = useState([]);
  const [offeringType, setOfferingType] = useState<'products' | 'services' | 'both'>('products');
  const [loading, setLoading] = useState(true);
  const [selectedPricelist, setSelectedPricelist] = useState(null);
  const [showPriceDialog, setShowPriceDialog] = useState(false);
  const [showNewListDialog, setShowNewListDialog] = useState(false);
  const [showEditListDialog, setShowEditListDialog] = useState(false);
  const [priceFormData, setPriceFormData] = useState({
    product_id: '',
    price: '',
    min_quantity: 1,
    notes: ''
  });
  const [newListData, setNewListData] = useState({
    name: '',
    description: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Storico prezzi
  const [activeTab, setActiveTab] = useState('pricelists');
  const [priceHistory, setPriceHistory] = useState([]);
  const [historyStats, setHistoryStats] = useState(null);
  const [historyDays, setHistoryDays] = useState(30);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Tutorial
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  
  // Wizard Crea Listino Guidato
  const [showWizard, setShowWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardType, setWizardType] = useState<'standard' | 'association' | 'buyer' | null>(null);
  const [wizardDiscount, setWizardDiscount] = useState(0);
  const [wizardName, setWizardName] = useState('');
  const [wizardBuyerId, setWizardBuyerId] = useState('');
  const [wizardItems, setWizardItems] = useState<any[]>([]);
  const [relationships, setRelationships] = useState<any[]>([]);
  const [creatingWizardList, setCreatingWizardList] = useState(false);

  // Carica relazioni per il wizard
  const fetchRelationships = async () => {
    try {
      const res = await axios.get(`${API}/relationships`);
      setRelationships(Array.isArray(res.data) ? res.data : res.data.relationships || []);
    } catch { /* ignore */ }
  };

  const openWizard = () => {
    setWizardStep(0);
    setWizardType(null);
    setWizardDiscount(0);
    setWizardName('');
    setWizardBuyerId('');
    setWizardItems([]);
    setShowWizard(true);
    fetchRelationships();
  };

  // Prepara items dal listino standard con sconto
  const prepareWizardItems = (discount: number) => {
    const standardList = pricelists.find(p => p.is_standard);
    if (!standardList?.items) return;
    const items = standardList.items.map(item => {
      const originalPrice = item.price;
      const discountedPrice = discount > 0 ? Math.round(originalPrice * (1 - discount / 100) * 100) / 100 : originalPrice;
      return { ...item, original_price: originalPrice, price: discountedPrice, custom_price: null };
    });
    setWizardItems(items);
  };

  const handleWizardDiscountChange = (val: number) => {
    setWizardDiscount(val);
    prepareWizardItems(val);
  };

  const handleWizardItemPriceChange = (productId: string, newPrice: string) => {
    setWizardItems(prev => prev.map(item => 
      item.product_id === productId 
        ? { ...item, price: parseFloat(newPrice) || 0, custom_price: parseFloat(newPrice) || 0 }
        : item
    ));
  };

  const handleCreateWizardList = async () => {
    if (!wizardName.trim()) { toast.error('Inserisci un nome per il listino'); return; }
    setCreatingWizardList(true);
    try {
      const listData: any = {
        name: wizardName.trim(),
        is_standard: false,
        items: wizardItems.map(({ original_price, custom_price, ...item }) => item)
      };
      if (wizardBuyerId) listData.buyer_id = wizardBuyerId;
      
      const res = await axios.post(`${API}/pricelists`, listData);
      toast.success(`Listino "${wizardName}" creato con ${wizardItems.length} prodotti!`);
      setShowWizard(false);
      fetchData();
      setSelectedPricelist(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nella creazione'));
    } finally {
      setCreatingWizardList(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchPriceHistory();
    }
  }, [activeTab, historyDays]);

  const fetchPriceHistory = async () => {
    setLoadingHistory(true);
    try {
      const [historyRes, statsRes] = await Promise.all([
        axios.get(`${API}/pricelists/history?days=${historyDays}`),
        axios.get(`${API}/pricelists/history/stats?days=${historyDays}`)
      ]);
      setPriceHistory(historyRes.data);
      setHistoryStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchData = async () => {
    try {
      const [pricelistsRes, productsRes] = await Promise.all([
        axios.get(`${API}/pricelists`),
        axios.get(`${API}/products`)
      ]);
      setPricelists(pricelistsRes.data);
      const allItems = productsRes.data;
      setProducts(allItems);
      
      // Detect offeringType from catalog items
      const hasProducts = allItems.some(p => !p.item_type || p.item_type === 'product');
      const hasServices = allItems.some(p => p.item_type === 'service');
      if (hasProducts && hasServices) setOfferingType('both');
      else if (hasServices) setOfferingType('services');
      else setOfferingType('products');
      
      // Select standard pricelist by default
      const standard = pricelistsRes.data.find(p => p.is_standard);
      if (standard) setSelectedPricelist(standard);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPriceDialog = (item = null) => {
    if (item) {
      setPriceFormData({
        product_id: item.product_id,
        price: item.price.toString(),
        min_quantity: item.min_quantity || 1,
        notes: item.notes || ''
      });
    } else {
      setPriceFormData({
        product_id: '',
        price: '',
        min_quantity: 1,
        notes: ''
      });
    }
    setShowPriceDialog(true);
  };

  const handleSavePrice = async () => {
    if (!priceFormData.product_id) {
      toast.error('Seleziona un prodotto o servizio');
      return;
    }
    const selectedItem = products.find(p => p.id === priceFormData.product_id);
    const isService = selectedItem?.item_type === 'service';
    if (!isService && !priceFormData.price) {
      toast.error('Inserisci il prezzo per questo prodotto');
      return;
    }

    setSaving(true);
    try {
      const product = products.find(p => p.id === priceFormData.product_id);
      const newItem = {
        product_id: priceFormData.product_id,
        price: parseFloat(String(priceFormData.price)),
        min_quantity: typeof priceFormData.min_quantity === 'string' 
          ? parseInt(priceFormData.min_quantity) || 1 
          : priceFormData.min_quantity || 1,
        notes: priceFormData.notes
      };

      // Update pricelist items
      const existingItems = selectedPricelist.items || [];
      const existingIndex = existingItems.findIndex(i => i.product_id === newItem.product_id);
      
      let newItems;
      if (existingIndex >= 0) {
        newItems = [...existingItems];
        newItems[existingIndex] = newItem;
      } else {
        newItems = [...existingItems, newItem];
      }

      await axios.put(`${API}/pricelists/${selectedPricelist.id}`, {
        ...selectedPricelist,
        items: newItems
      });

      toast.success('Prezzo salvato');
      setShowPriceDialog(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePrice = async (productId) => {
    if (!await confirmAction({ title: 'Rimuovi prezzo', description: 'Rimuovere questo prezzo dal listino?', confirmText: 'Rimuovi', variant: 'destructive' })) return;

    try {
      const newItems = selectedPricelist.items.filter(i => i.product_id !== productId);
      await axios.put(`${API}/pricelists/${selectedPricelist.id}`, {
        ...selectedPricelist,
        items: newItems
      });
      toast.success('Prezzo rimosso');
      fetchData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  // Crea nuovo listino
  const handleCreateList = async () => {
    if (!newListData.name.trim()) {
      toast.error('Inserisci un nome per il listino');
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post(`${API}/pricelists`, {
        name: newListData.name.trim(),
        is_standard: false,
        items: []
      });
      toast.success(`Listino "${newListData.name}" creato!`);
      setShowNewListDialog(false);
      setNewListData({ name: '', description: '' });
      fetchData();
      // Seleziona il nuovo listino
      setSelectedPricelist(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nella creazione'));
    } finally {
      setSaving(false);
    }
  };

  // Rinomina listino
  const handleRenameList = async () => {
    if (!newListData.name.trim()) {
      toast.error('Inserisci un nome per il listino');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/pricelists/${selectedPricelist.id}`, {
        ...selectedPricelist,
        name: newListData.name.trim()
      });
      toast.success('Listino rinominato');
      setShowEditListDialog(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel salvataggio'));
    } finally {
      setSaving(false);
    }
  };

  // Elimina listino
  const handleDeleteList = async (listId) => {
    const list = pricelists.find(p => p.id === listId);
    if (list?.is_standard) {
      toast.error('Non puoi eliminare il listino standard');
      return;
    }
    
    if (!await confirmAction({ title: 'Elimina listino', description: `Sei sicuro di voler eliminare il listino "${list?.name}"? Questa azione non può essere annullata.`, confirmText: 'Elimina', variant: 'destructive' })) return;

    try {
      await axios.delete(`${API}/pricelists/${listId}`);
      toast.success('Listino eliminato');
      if (selectedPricelist?.id === listId) {
        const standard = pricelists.find(p => p.is_standard);
        setSelectedPricelist(standard || null);
      }
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nell'eliminazione"));
    }
  };

  // Imposta come standard
  const handleSetAsStandard = async (listId) => {
    try {
      // Prima rimuovi standard dagli altri
      for (const pl of pricelists) {
        if (pl.is_standard && pl.id !== listId) {
          await axios.put(`${API}/pricelists/${pl.id}`, {
            ...pl,
            is_standard: false
          });
        }
      }
      // Poi imposta questo come standard
      const list = pricelists.find(p => p.id === listId);
      await axios.put(`${API}/pricelists/${listId}`, {
        ...list,
        is_standard: true
      });
      toast.success('Listino impostato come predefinito');
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'impostazione');
    }
  };

  // Apri dialog modifica nome
  const openEditDialog = () => {
    setNewListData({ name: selectedPricelist?.name || '', description: '' });
    setShowEditListDialog(true);
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product?.name || 'Prodotto sconosciuto';
  };

  const getProductUnit = (productId) => {
    const product = products.find(p => p.id === productId);
    return product?.unit_of_measure || 'pz';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pricelists-page">
      <ConfirmDialog />
      {/* Banner Introduttivo - sempre visibile */}
      <div className="rounded-xl bg-gradient-to-br from-primary/5 via-background to-amber-50/50 dark:from-primary/10 dark:via-background dark:to-amber-950/10 border p-5">
        <div className="flex items-start gap-4 max-w-5xl">
          <div className="bg-primary/10 rounded-xl p-3 shrink-0">
            <Tag className="h-7 w-7 text-primary" />
          </div>
          <div className="flex-1 space-y-2">
            <h2 className="text-lg font-semibold">
              {offeringType === 'services' ? 'I tuoi Listini Prezzi Servizi' : 'I tuoi Listini Prezzi'}
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl">
              {offeringType === 'products' && (
                <>
                  Qui gestisci i prezzi dei tuoi <strong>prodotti</strong>. Hai un <strong>listino standard</strong> visibile a tutti i clienti, 
                  e puoi creare <strong>listini personalizzati</strong> con prezzi dedicati per singoli clienti 
                  o per un'intera associazione — ad esempio con uno sconto riservato ai soci FIDA. 
                  I clienti vedranno automaticamente il prezzo migliore quando fanno un ordine.
                </>
              )}
              {offeringType === 'services' && (
                <>
                  Qui gestisci i prezzi dei tuoi <strong>servizi</strong>. Hai un <strong>listino standard</strong> con le tue tariffe di base, 
                  e puoi creare <strong>listini personalizzati</strong> con tariffe dedicate per singoli clienti 
                  o per gruppi — ad esempio con uno sconto riservato ai soci. 
                  Ricorda: per i servizi il prezzo è <strong>facoltativo</strong>, puoi anche gestirlo caso per caso.
                </>
              )}
              {offeringType === 'both' && (
                <>
                  Qui gestisci i prezzi dei tuoi <strong>prodotti e servizi</strong>. Hai un <strong>listino standard</strong> con i tuoi prezzi di base, 
                  e puoi creare <strong>listini personalizzati</strong> per singoli clienti o associazioni. 
                  Nel menu a tendina quando aggiungi un prezzo trovi sia i prodotti che i servizi. 
                  Per i servizi il prezzo rimane <strong>facoltativo</strong>.
                </>
              )}
            </p>
            <div className="flex items-center gap-3 pt-1">
              <Button size="sm" onClick={() => { setTutorialStep(0); setShowTutorial(true); }} className="gap-2">
                <MousePointerClick className="h-4 w-4" /> Scopri come funziona
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">{t('pricelists.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {pricelists.length} listini · {
              offeringType === 'products' ? `${products.filter(p => !p.item_type || p.item_type === 'product').length} prodotti` :
              offeringType === 'services' ? `${products.filter(p => p.item_type === 'service').length} servizi` :
              `${products.filter(p => !p.item_type || p.item_type === 'product').length} prodotti · ${products.filter(p => p.item_type === 'service').length} servizi`
            } nel catalogo
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openWizard} className="gap-2">
            <Copy className="h-4 w-4" /> Crea Listino Personalizzato
          </Button>
          <Button onClick={() => setShowNewListDialog(true)} className="gap-2">
            <Plus className="h-4 w-4" /> Nuovo Listino Vuoto
          </Button>
        </div>
      </div>

      {/* Tabs: Listini / Storico */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="pricelists" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Listini
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2" data-testid="history-tab">
            <History className="h-4 w-4" />
            Storico Prezzi
          </TabsTrigger>
        </TabsList>

        <TabsContent value="history">
          {/* Storico Prezzi Content */}
          <div className="space-y-6">
            {/* Stats Cards */}
            {historyStats && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/50">
                        <History className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{historyStats.total_changes}</p>
                        <p className="text-sm text-muted-foreground">Modifiche totali</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/50">
                        <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{historyStats.price_increases}</p>
                        <p className="text-sm text-muted-foreground">Aumenti</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/50">
                        <TrendingDown className="h-5 w-5 text-red-600 dark:text-red-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{historyStats.price_decreases}</p>
                        <p className="text-sm text-muted-foreground">Riduzioni</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/50">
                        <Package className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{historyStats.avg_change_percent > 0 ? '+' : ''}{historyStats.avg_change_percent}%</p>
                        <p className="text-sm text-muted-foreground">Media variazione</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Period Selector */}
            <div className="flex items-center gap-4">
              <Label>Periodo:</Label>
              <Select value={historyDays.toString()} onValueChange={(v) => setHistoryDays(parseInt(v))}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Ultimi 7 giorni</SelectItem>
                  <SelectItem value="30">Ultimi 30 giorni</SelectItem>
                  <SelectItem value="90">Ultimi 90 giorni</SelectItem>
                  <SelectItem value="365">Ultimo anno</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* History Table */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Cronologia Modifiche</CardTitle>
              </CardHeader>
              <CardContent>
                {loadingHistory ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  </div>
                ) : priceHistory.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <History className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>Nessuna modifica di prezzo nel periodo selezionato</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Prodotto</TableHead>
                        <TableHead>Listino</TableHead>
                        <TableHead className="text-right">Prezzo Precedente</TableHead>
                        <TableHead className="text-right">Nuovo Prezzo</TableHead>
                        <TableHead className="text-right">Variazione</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {priceHistory.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="text-muted-foreground">
                            {new Date(entry.created_at).toLocaleDateString('it-IT', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{entry.product_name}</p>
                              {entry.product_sku && (
                                <p className="text-xs text-muted-foreground">{entry.product_sku}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">{entry.pricelist_name}</TableCell>
                          <TableCell className="text-right">
                            {entry.old_price > 0 ? `€${entry.old_price.toFixed(2)}` : '-'}
                          </TableCell>
                          <TableCell className="text-right font-medium">€{entry.new_price.toFixed(2)}</TableCell>
                          <TableCell className="text-right">
                            {entry.change_type === 'create' ? (
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                                <Plus className="h-3 w-3 mr-1" />
                                Nuovo
                              </Badge>
                            ) : entry.change_percent > 0 ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 dark:bg-green-900/50 dark:text-green-300">
                                <TrendingUp className="h-3 w-3 mr-1" />
                                +{entry.change_percent}%
                              </Badge>
                            ) : entry.change_percent < 0 ? (
                              <Badge variant="outline" className="bg-red-50 text-red-700 dark:bg-red-900/50 dark:text-red-300">
                                <TrendingDown className="h-3 w-3 mr-1" />
                                {entry.change_percent}%
                              </Badge>
                            ) : (
                              <Badge variant="outline">
                                <Minus className="h-3 w-3 mr-1" />
                                0%
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Most Changed Products */}
            {historyStats?.most_changed_products?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading">Prodotti più modificati</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {historyStats.most_changed_products.map((product, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <span>{product.name}</span>
                        <Badge variant="secondary">{product.changes} modifiche</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="pricelists">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Pricelist Selection */}
        <Card className="lg:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="font-heading text-lg">I tuoi listini</CardTitle>
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => {
                setNewListData({ name: '', description: '' });
                setShowNewListDialog(true);
              }}
              data-testid="new-pricelist-btn"
            >
              <FolderPlus className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {pricelists.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nessun listino</p>
                <Button 
                  variant="link" 
                  className="mt-2"
                  onClick={() => setShowNewListDialog(true)}
                >
                  Crea il primo listino
                </Button>
              </div>
            ) : (
              pricelists.map((pl) => (
                <div
                  key={pl.id}
                  className={`group relative p-3 rounded-lg transition-colors cursor-pointer ${
                    selectedPricelist?.id === pl.id 
                      ? 'bg-primary text-primary-foreground' 
                      : 'hover:bg-muted'
                  }`}
                  onClick={() => setSelectedPricelist(pl)}
                  data-testid={`pricelist-${pl.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {pl.is_standard && <Star className="h-4 w-4 shrink-0" />}
                      <span className="font-medium truncate">{pl.name}</span>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className={`h-8 w-8 opacity-0 group-hover:opacity-100 ${
                            selectedPricelist?.id === pl.id ? 'text-primary-foreground hover:bg-primary-foreground/20' : ''
                          }`}
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPricelist(pl);
                          setNewListData({ name: pl.name, description: '' });
                          setShowEditListDialog(true);
                        }}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Rinomina
                        </DropdownMenuItem>
                        {!pl.is_standard && (
                          <DropdownMenuItem onClick={(e) => {
                            e.stopPropagation();
                            handleSetAsStandard(pl.id);
                          }}>
                            <Star className="h-4 w-4 mr-2" />
                            Imposta come predefinito
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        {!pl.is_standard && (
                          <DropdownMenuItem 
                            className="text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteList(pl.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Elimina
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <span className={`text-xs ${selectedPricelist?.id === pl.id ? 'opacity-70' : 'text-muted-foreground'}`}>
                    {pl.items?.length || 0} {t('pricelists.itemCount')}
                  </span>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Pricelist Items */}
        <Card className="lg:col-span-3">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                {selectedPricelist?.is_standard && <Star className="h-5 w-5 text-amber-500" />}
                {selectedPricelist?.name || 'Seleziona un listino'}
              </CardTitle>
              {selectedPricelist?.is_standard && (
                <p className="text-sm text-muted-foreground mt-1">
                  Listino base - assegnato di default ai nuovi clienti
                </p>
              )}
            </div>
            {selectedPricelist && (
              <Button onClick={() => handleOpenPriceDialog()} data-testid="add-price-btn">
                <Plus className="mr-2 h-4 w-4" />
                {t('pricelists.addItem')}
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {selectedPricelist?.items?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="uppercase text-xs">Prodotto</TableHead>
                    <TableHead className="uppercase text-xs text-right">{t('common.price')}</TableHead>
                    <TableHead className="uppercase text-xs text-center">{t('pricelists.minQuantity')}</TableHead>
                    <TableHead className="uppercase text-xs">{t('common.notes')}</TableHead>
                    <TableHead className="uppercase text-xs">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedPricelist.items.map((item) => (
                    <TableRow key={item.product_id} data-testid={`price-item-${item.product_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{getProductName(item.product_id)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        €{item.price.toFixed(2)}/{getProductUnit(item.product_id)}
                      </TableCell>
                      <TableCell className="text-center">
                        {item.min_quantity || 1}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {item.notes || '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleOpenPriceDialog(item)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleRemovePrice(item.product_id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Nessun prezzo configurato</p>
                <p className="text-sm mt-1">Aggiungi i prezzi per i tuoi prodotti</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
        </TabsContent>
      </Tabs>

      {/* Price Dialog */}
      <Dialog open={showPriceDialog} onOpenChange={setShowPriceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">{t('pricelists.addItem')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>
                {offeringType === 'services' ? 'Servizio *' : offeringType === 'both' ? 'Prodotto o Servizio *' : 'Prodotto *'}
              </Label>
              <Select 
                value={priceFormData.product_id} 
                onValueChange={(v) => setPriceFormData({ ...priceFormData, product_id: v })}
              >
                <SelectTrigger data-testid="price-product-select">
                  <SelectValue placeholder={offeringType === 'services' ? 'Seleziona servizio' : 'Seleziona prodotto'} />
                </SelectTrigger>
                <SelectContent>
                  {offeringType === 'both' ? (
                    <>
                      {products.filter(p => !p.item_type || p.item_type === 'product').length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Prodotti</div>
                          {products.filter(p => !p.item_type || p.item_type === 'product').map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name} ({p.unit_of_measure || 'pz'})</SelectItem>
                          ))}
                        </>
                      )}
                      {products.filter(p => p.item_type === 'service').length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-t mt-1 pt-2">Servizi</div>
                          {products.filter(p => p.item_type === 'service').map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name} ({p.unit_of_measure || 'ore'})</SelectItem>
                          ))}
                        </>
                      )}
                    </>
                  ) : (
                    products.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} ({p.unit_of_measure || 'pz'})
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="price">
                  {t('common.price')} (€)
                  {offeringType === 'services' && <span className="text-xs text-muted-foreground font-normal ml-1">(opzionale per servizi)</span>}
                </Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={priceFormData.price}
                  onChange={(e) => setPriceFormData({ ...priceFormData, price: e.target.value })}
                  data-testid="price-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="min_quantity">{t('pricelists.minQuantity')}</Label>
                <Input
                  id="min_quantity"
                  type="number"
                  min="1"
                  value={priceFormData.min_quantity}
                  onChange={(e) => setPriceFormData({ ...priceFormData, min_quantity: parseInt(e.target.value) || 1 })}
                  data-testid="min-quantity-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">{t('common.notes')}</Label>
              <Input
                id="notes"
                value={priceFormData.notes}
                onChange={(e) => setPriceFormData({ ...priceFormData, notes: e.target.value })}
                placeholder="es. Prezzo promozionale, Sconto quantità..."
                data-testid="price-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPriceDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleSavePrice}
              disabled={saving}
              data-testid="save-price-btn"
            >
              {saving ? t('common.loading') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Pricelist Dialog */}
      <Dialog open={showNewListDialog} onOpenChange={setShowNewListDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <FolderPlus className="h-5 w-5" />
              Crea Nuovo Listino
            </DialogTitle>
            <DialogDescription>
              Crea un nuovo listino prezzi. Potrai poi aggiungere i prodotti e i relativi prezzi.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="list-name">Nome Listino *</Label>
              <Input
                id="list-name"
                value={newListData.name}
                onChange={(e) => setNewListData({ ...newListData, name: e.target.value })}
                placeholder="es. Listino Vini, Sottaceti Premium, Prodotti Bio..."
                data-testid="new-list-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewListDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleCreateList}
              disabled={saving || !newListData.name.trim()}
              data-testid="create-list-btn"
            >
              {saving ? t('common.loading') : 'Crea Listino'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Pricelist Name Dialog */}
      <Dialog open={showEditListDialog} onOpenChange={setShowEditListDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Rinomina Listino
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-list-name">Nome Listino *</Label>
              <Input
                id="edit-list-name"
                value={newListData.name}
                onChange={(e) => setNewListData({ ...newListData, name: e.target.value })}
                placeholder="es. Listino Vini, Sottaceti Premium..."
                data-testid="edit-list-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditListDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={handleRenameList}
              disabled={saving || !newListData.name.trim()}
              data-testid="save-list-name-btn"
            >
              {saving ? t('common.loading') : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tutorial Dialog */}
      <Dialog open={showTutorial} onOpenChange={setShowTutorial}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-primary" />
              {tutorialStep === 0 && "Come funzionano i Listini"}
              {tutorialStep === 1 && "Il Listino Standard"}
              {tutorialStep === 2 && "Listini Personalizzati"}
              {tutorialStep === 3 && "Sconto forfettario + variazioni singole"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {tutorialStep === 0 && (
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-xl p-5 text-center"><Tag className="h-12 w-12 text-primary mx-auto mb-3" /><p className="font-medium text-lg">I tuoi prezzi, organizzati</p></div>
                <p className="text-sm text-muted-foreground leading-relaxed">I listini ti permettono di <strong>stabilire i prezzi</strong> dei tuoi prodotti. Ogni cliente vedrà il prezzo che hai deciso per lui.</p>
                <p className="text-sm text-muted-foreground leading-relaxed">Hai un <strong>listino standard</strong> (il prezzo base per tutti) e puoi creare <strong>listini personalizzati</strong> con prezzi diversi per clienti o associazioni specifiche.</p>
                <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg p-3"><p className="text-sm text-green-800 dark:text-green-300"><strong>Il listino standard viene creato automaticamente</strong> quando importi il catalogo. Se hai già inserito i prezzi nel catalogo, sei già a buon punto!</p></div>
              </div>
            )}
            {tutorialStep === 1 && (
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-xl p-5 text-center"><Star className="h-12 w-12 text-amber-500 mx-auto mb-3" /><p className="font-medium text-lg">Il Listino Standard</p></div>
                <p className="text-sm text-muted-foreground leading-relaxed">È il <strong>prezzo di riferimento</strong> che vedono tutti i clienti che non hanno un listino personalizzato.</p>
                <div className="space-y-2">
                  <div className="flex items-start gap-3"><div className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shrink-0">1</div><p className="text-sm text-muted-foreground">Viene creato in automatico quando aggiungi prodotti al catalogo</p></div>
                  <div className="flex items-start gap-3"><div className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shrink-0">2</div><p className="text-sm text-muted-foreground">Puoi modificare i prezzi in qualsiasi momento</p></div>
                  <div className="flex items-start gap-3"><div className="bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shrink-0">3</div><p className="text-sm text-muted-foreground">Se un cliente ha un listino personalizzato, vedrà quei prezzi al posto dello standard</p></div>
                </div>
              </div>
            )}
            {tutorialStep === 2 && (
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-xl p-5 text-center"><Users className="h-12 w-12 text-primary mx-auto mb-3" /><p className="font-medium text-lg">Listini Personalizzati</p></div>
                <p className="text-sm text-muted-foreground leading-relaxed">Puoi creare listini su misura per:</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="border rounded-lg p-3 text-center"><Building2 className="h-6 w-6 text-blue-500 mx-auto mb-2" /><p className="text-sm font-medium">Un'Associazione</p><p className="text-xs text-muted-foreground">Es. sconto FIDA -10%</p></div>
                  <div className="border rounded-lg p-3 text-center"><Users className="h-6 w-6 text-purple-500 mx-auto mb-2" /><p className="text-sm font-medium">Un singolo Buyer</p><p className="text-xs text-muted-foreground">Es. Ristorante Da Marco</p></div>
                </div>
                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3"><p className="text-sm text-blue-800 dark:text-blue-300"><strong>Il cliente vedrà automaticamente il prezzo migliore</strong> quando consulta il tuo catalogo per fare un ordine.</p></div>
              </div>
            )}
            {tutorialStep === 3 && (
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-xl p-5 text-center"><Percent className="h-12 w-12 text-primary mx-auto mb-3" /><p className="font-medium text-lg">Sconto e variazioni</p></div>
                <p className="text-sm text-muted-foreground leading-relaxed">Quando crei un listino personalizzato puoi:</p>
                <div className="space-y-2">
                  <div className="flex items-start gap-3"><Check className="h-5 w-5 text-green-500 shrink-0 mt-0.5" /><p className="text-sm text-muted-foreground"><strong>Applicare uno sconto forfettario</strong> su tutti i prodotti (es. -10% su tutto il catalogo)</p></div>
                  <div className="flex items-start gap-3"><Check className="h-5 w-5 text-green-500 shrink-0 mt-0.5" /><p className="text-sm text-muted-foreground"><strong>Modificare i prezzi singolarmente</strong> per fare eccezioni (es. la Mozzarella a prezzo speciale)</p></div>
                  <div className="flex items-start gap-3"><Check className="h-5 w-5 text-green-500 shrink-0 mt-0.5" /><p className="text-sm text-muted-foreground"><strong>Combinare entrambi</strong>: sconto generale + variazioni puntuali</p></div>
                </div>
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3"><p className="text-sm text-amber-800 dark:text-amber-300"><strong>Prova subito!</strong> Clicca "Crea Listino Personalizzato" e segui la procedura guidata.</p></div>
              </div>
            )}
          </div>
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <div className="flex items-center gap-1.5">{[0,1,2,3].map(i => (<button key={i} onClick={() => setTutorialStep(i)} className={cn("w-2 h-2 rounded-full transition-all", tutorialStep === i ? "bg-primary w-6" : "bg-muted-foreground/30")} />))}</div>
            <div className="flex items-center gap-2">
              {tutorialStep > 0 && <Button variant="ghost" size="sm" onClick={() => setTutorialStep(tutorialStep - 1)}>Indietro</Button>}
              {tutorialStep < 3 ? (<Button size="sm" onClick={() => setTutorialStep(tutorialStep + 1)}>Avanti<ArrowRight className="h-4 w-4 ml-1" /></Button>) : (<Button size="sm" onClick={() => setShowTutorial(false)}>Ho capito!</Button>)}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wizard Crea Listino Personalizzato */}
      <Dialog open={showWizard} onOpenChange={setShowWizard}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5 text-primary" />
              Crea Listino Personalizzato
            </DialogTitle>
            <DialogDescription>
              {wizardStep === 0 && "Per chi vuoi creare questo listino?"}
              {wizardStep === 1 && "Dai un nome e applica uno sconto"}
              {wizardStep === 2 && "Rivedi i prezzi e personalizza"}
            </DialogDescription>
          </DialogHeader>

          {/* Step 0: Tipo */}
          {wizardStep === 0 && (
            <div className="grid grid-cols-1 gap-3 py-4">
              <button onClick={() => { setWizardType('buyer'); setWizardStep(1); prepareWizardItems(0); }} className="flex items-start gap-4 p-4 border rounded-xl hover:border-primary hover:bg-primary/5 transition-all text-left">
                <div className="bg-purple-100 dark:bg-purple-900/30 rounded-xl p-3"><Users className="h-6 w-6 text-purple-600" /></div>
                <div><p className="font-semibold">Per un singolo cliente (Buyer)</p><p className="text-sm text-muted-foreground">Crea prezzi dedicati per un ristorante, un negozio o un cliente specifico</p></div>
              </button>
              <button onClick={() => { setWizardType('association'); setWizardStep(1); prepareWizardItems(0); }} className="flex items-start gap-4 p-4 border rounded-xl hover:border-primary hover:bg-primary/5 transition-all text-left">
                <div className="bg-blue-100 dark:bg-blue-900/30 rounded-xl p-3"><Building2 className="h-6 w-6 text-blue-600" /></div>
                <div><p className="font-semibold">Per un'associazione</p><p className="text-sm text-muted-foreground">Crea prezzi riservati per tutti i membri di un'associazione (es. FIDA, Confesercenti...)</p></div>
              </button>
              <button onClick={() => { setWizardType('standard'); setWizardStep(1); prepareWizardItems(0); }} className="flex items-start gap-4 p-4 border rounded-xl hover:border-primary hover:bg-primary/5 transition-all text-left">
                <div className="bg-green-100 dark:bg-green-900/30 rounded-xl p-3"><Tag className="h-6 w-6 text-green-600" /></div>
                <div><p className="font-semibold">Listino generico</p><p className="text-sm text-muted-foreground">Crea un listino aggiuntivo senza assegnarlo a nessuno (potrai farlo dopo)</p></div>
              </button>
            </div>
          )}

          {/* Step 1: Nome + Sconto */}
          {wizardStep === 1 && (
            <div className="space-y-5 py-4">
              <div className="space-y-2">
                <Label className="font-medium">Nome del listino *</Label>
                <Input value={wizardName} onChange={(e) => setWizardName(e.target.value)} placeholder={wizardType === 'buyer' ? 'Es. Listino Ristorante Da Marco' : wizardType === 'association' ? 'Es. Listino FIDA -10%' : 'Es. Listino Promozionale Estate'} />
              </div>

              {wizardType === 'buyer' && (
                <div className="space-y-2">
                  <Label className="font-medium">Seleziona il cliente</Label>
                  <Select value={wizardBuyerId} onValueChange={setWizardBuyerId}>
                    <SelectTrigger><SelectValue placeholder="Scegli un cliente..." /></SelectTrigger>
                    <SelectContent>
                      {relationships.filter(r => r.status === 'active').map(rel => (
                        <SelectItem key={rel.id} value={rel.buyer_id}>{rel.buyer_company_name || rel.buyer_name || rel.buyer_id}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-3 p-4 border rounded-xl bg-muted/30">
                <div className="flex items-center justify-between">
                  <Label className="font-medium flex items-center gap-2"><Percent className="h-4 w-4" /> Sconto forfettario su tutti i prodotti</Label>
                  <span className="text-2xl font-bold text-primary">{wizardDiscount}%</span>
                </div>
                <Slider value={[wizardDiscount]} onValueChange={(v) => handleWizardDiscountChange(v[0])} max={50} step={1} className="mt-2" />
                <p className="text-xs text-muted-foreground">Trascina per applicare uno sconto uniforme. Al passo successivo potrai modificare i prezzi singolarmente.</p>
              </div>

              <div className="flex justify-between pt-2">
                <Button variant="ghost" onClick={() => setWizardStep(0)}>Indietro</Button>
                <Button onClick={() => { if (!wizardName.trim()) { toast.error('Inserisci un nome'); return; } setWizardStep(2); }}>Avanti — Rivedi prezzi<ArrowRight className="h-4 w-4 ml-1" /></Button>
              </div>
            </div>
          )}

          {/* Step 2: Rivedi prezzi */}
          {wizardStep === 2 && (
            <div className="space-y-4 py-2">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">{wizardItems.length} prodotti · {wizardDiscount > 0 ? `Sconto ${wizardDiscount}% applicato` : 'Nessuno sconto applicato'}</p>
                <Badge variant="outline">{wizardName}</Badge>
              </div>
              <div className="max-h-[40vh] overflow-y-auto border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Prodotto</TableHead>
                      <TableHead className="text-right">Prezzo standard</TableHead>
                      <TableHead className="text-right w-36">Nuovo prezzo</TableHead>
                      <TableHead className="text-right w-20">Var.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {wizardItems.map(item => {
                      const product = products.find(p => p.id === item.product_id);
                      const variation = item.original_price > 0 ? Math.round((item.price - item.original_price) / item.original_price * 100) : 0;
                      return (
                        <TableRow key={item.product_id}>
                          <TableCell className="font-medium">{product?.name || '—'}</TableCell>
                          <TableCell className="text-right text-muted-foreground">€ {item.original_price?.toFixed(2)}</TableCell>
                          <TableCell className="text-right">
                            <Input type="number" step="0.01" value={item.price} onChange={(e) => handleWizardItemPriceChange(item.product_id, e.target.value)} className="h-8 w-28 text-right ml-auto" />
                          </TableCell>
                          <TableCell className={cn("text-right text-sm font-medium", variation < 0 ? "text-red-500" : variation > 0 ? "text-green-500" : "text-muted-foreground")}>
                            {variation !== 0 ? `${variation > 0 ? '+' : ''}${variation}%` : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="flex justify-between pt-2">
                <Button variant="ghost" onClick={() => setWizardStep(1)}>Indietro</Button>
                <Button onClick={handleCreateWizardList} disabled={creatingWizardList} className="gap-2">
                  {creatingWizardList ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                  Crea Listino ({wizardItems.length} prodotti)
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default PricelistsPage;
